import React, { useState, useRef, useEffect } from "react";
import { Plus, Layers, FileJson } from "lucide-react";
import { BLOCK_TYPES, getBlockColors } from "./blocks.data";

const MenuGrid = ({ onPick }) => (
  <div className="absolute z-20 top-full mt-2 left-1/2 -translate-x-1/2 w-80 max-w-[90vw] rounded-2xl border border-border bg-surface shadow-xl p-2">
    <p className="px-2 pt-1.5 pb-2 text-[10.5px] font-bold uppercase tracking-wide text-text_muted">
      Insert Content Block
    </p>
    <div className="grid grid-cols-2 gap-1">
      {BLOCK_TYPES.map((bt) => {
        const Icon = bt.icon;
        const colors = getBlockColors(bt.type);
        return (
          <button
            key={bt.type}
            type="button"
            onClick={() => onPick(bt.type)}
            className="flex flex-col items-start gap-1.5 p-2.5 rounded-xl border border-transparent text-left"
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg}`}>
              <Icon size={15} className={colors.text} />
            </div>
            <div className="min-w-0">
              <p className="text-[12.5px] font-semibold text-text_primary">{bt.label}</p>
              <p className="text-[10.5px] text-text_muted truncate">{bt.description}</p>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

// variant "inline": thin always-visible insertion line between blocks
// variant "full": bigger standalone call-to-action (used for the empty state)
const AddContentMenu = ({ onAdd, onImport, variant = "inline" }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handlePick = (type) => {
    onAdd(type);
    setOpen(false);
  };

  if (variant === "full") {
    return (
      <div ref={wrapRef} className="relative flex justify-center">
        <div className="w-full flex flex-col items-center text-center py-14 px-6 rounded-2xl border border-dashed border-border bg-surface_alt/40">
          <div className="w-14 h-14 rounded-2xl bg-primary_tint flex items-center justify-center mb-4">
            <Layers size={24} className="text-primary" />
          </div>
          <h3 className="text-[15px] font-semibold text-text_primary">Start building your article</h3>
          <p className="text-[13px] text-text_muted mt-1 max-w-sm">
            Add headings, text, images, code, and more to structure your content, or import a
            ready-made content JSON payload.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary shadow-sm"
            >
              <Plus size={15} />
              Add Content
            </button>
            {onImport && (
              <button
                type="button"
                onClick={onImport}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-surface text-sm font-semibold text-text_secondary shadow-sm"
              >
                <FileJson size={15} />
                Import JSON
              </button>
            )}
          </div>
        </div>
        {open && <MenuGrid onPick={handlePick} />}
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative flex justify-center py-1.5 group">
      <div className="w-full flex items-center gap-2">
        <div className="flex-1 h-px bg-border" />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-shrink-0 w-7 h-7 rounded-full border border-border bg-surface text-text_muted flex items-center justify-center shadow-sm"
          title="Add content block"
        >
          <Plus size={14} />
        </button>
        <div className="flex-1 h-px bg-border" />
      </div>
      {open && <MenuGrid onPick={handlePick} />}
    </div>
  );
};

export default AddContentMenu;
